import React from "react";
import { Alert, Button } from 'react-bootstrap';
import Collapse from 'react-bootstrap/Collapse'
import FullQuoteActions from "../../state/fullQuoteActions";
import { IQuoteStepState } from "../../components/interfaces";
import { IAppState } from "../../state/interfaces";
import { connect } from "react-redux";
import { FULL_QUOTE_STEPS, COMPONENTS, YES_NO, QUOTE_QUESTIONS } from "../../AppConstants";
import { ValidatableForm } from "../../components/ValidatableForm";
import { ValidatableFormGroup } from "../../components/ValidatableFormGroup";
import { IVehicleStepProps } from "../../components/interfaces";
import Utilities from "../../classes/Utilities";
import { validationUtils } from "../../validation/Validation";
import { QuoteStep } from "./QuoteStepLayout";
import ButtonContinue from "../../components/ButtonContinue";
import QuoteStepBase from '../../components/QuoteStepBase';
import { IPerson, IAddressChangeResult, ILOBApiClient, IQuoteApiClient, IPaths, IToolTipOption } from "../../classes/Domain";
import { IVehicle, IUpdateVehicleResult } from "../../classes/ExternalQuote";
import VehicleTitle from '../../components/VehicleTitle';
import Question from '../../components/Question'
import Address from "../../components/Address";
import QuoteActions from "../../state/appActions";
import * as _ from 'lodash';
import VehicleExplorer from "../../classes/VehicleExplorer";
import Select from "react-select/src/Select";

const VEHICLE_OPTION_TYPES = {
    YEAR: "YEAR",
    MAKE: "MAKE",
    MODEL: "MODEL",
    BODY: "BODY",
    ENGINE: "ENGINE",
    CYLINDERS: "CYLINDERS",
    SPECS: "SPECS",
    FINISH: "FINISH"
};

const NO_OPTIONS = "No_options";

const START_YEAR = 1980;

type VehicleOption = {
    value: string,
    label: string,
    type: string,
    icon?: string
}

type OptionsGroup = {
    label: string,
    options: Array<VehicleOption>;
}

class VinRecord {
    year: number;
    make: string;
    model: string;
    body: string;
    engineSize: string;
    specsCombined: string;
    vin: string;
    cylinders: number;
    antiThft: string;
    compSymbol: string;
    collSymbol: string;
    costNewId: string;

    constructor() {
        this.year = 0;
        this.cylinders = 0;
        this.make = "";
        this.model = "";
        this.body = "";
        this.engineSize = "";
        this.specsCombined = "";
        this.vin = "";
        this.antiThft = "";
        this.compSymbol = "";
        this.collSymbol = "";
        this.costNewId = "";
    }
}

interface IAppStateUpdate {
    quoteId: number,
    paymentPlanCode: number,
    updateLienholder: boolean,
    updateGaragingAddress: boolean,
    addressSynchronization: boolean,
    stateCode: string,
    vehicle: IVehicle;
}

interface IVehicleUpdate {
    quoteRerated: boolean,
    vehicleChanged: boolean,
    quoteId: number,
    vehicleId: number;
}

interface IState extends IQuoteStepState {
    hasLienholder: string,
    updateLienholder: boolean,
    updateGaragingAddress: boolean,
    invalidGaragingAddress: boolean,
    invalidLienholderAddress: boolean,
    addressVerification: string,
    updateResult: IUpdateVehicleResult | null,
    vehicle: IVehicle | null,
    wasSalvaged: string,
    showSalvageWarning: boolean,
    salvageWarningWasDemonstrated: boolean,
    vinValid: boolean,
    options: Array<OptionsGroup>,
    selectedOptions: Array<VehicleOption>,
    loadingOptions: boolean;
}

interface IProps extends IVehicleStepProps {
    quoteId: number,
    paymentPlanCode: number,
    vehiclesCount: number,
    currentVehicle: IVehicle,
    insured: IPerson,
    reloadVehicle: (update: IVehicleUpdate) => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class Car extends QuoteStepBase<IProps, IState> {

    vehicleExplorer: VehicleExplorer;
    vinRecords: Array<VinRecord>;
    apiClient: IQuoteApiClient;

    constructor(props: IProps) {
        super(props);
        this.apiClient = this.props.quoteApiClient;
        this.vehicleExplorer = new VehicleExplorer();
        this.vinRecords = [];

        this.vinStepHandler = this.vinStepHandler.bind(this);
        this.next = this.next.bind(this);
        this.onAddressChanged = this.onAddressChanged.bind(this);
        this.onLienholderAddressChanged = this.onLienholderAddressChanged.bind(this);
        this.onLienholderChange = this.onLienholderChange.bind(this);
        this.onAddressVerificationChanged = this.onAddressVerificationChanged.bind(this);
        this.onVinChanged = this.onVinChanged.bind(this);
        this.onSalvagedChanged = this.onSalvagedChanged.bind(this);
        this.confirmSalvageWarning = this.confirmSalvageWarning.bind(this);

        this.state = {
            vehicle: null,
            hasLienholder: YES_NO.NO,
            updateLienholder: false,
            updateGaragingAddress: true,
            addressVerification: YES_NO.YES,
            invalidGaragingAddress: false,
            invalidLienholderAddress: false,
            updatingStore: false,
            errors: null,
            updateResult: null,
            wasSalvaged: "",
            showSalvageWarning: false,
            salvageWarningWasDemonstrated: false,
            vinValid: true,
            options: [],
            selectedOptions: [],
            loadingOptions: false
        };
    }

    componentDidMount() {
        this.props.reportCurrentStep();

        this.setState({ ...this.state, updatingStore: false, errors: null });

        validationUtils.subscribeToValidationFailure(FULL_QUOTE_STEPS.CARS,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, errors: validationData });
            });
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(FULL_QUOTE_STEPS.CARS);
    }

    static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            const vehicle = { ...nextProps.currentVehicle };

            if (!vehicle.garagingAddress) {
                vehicle.garagingAddress = nextProps.currentVehicle.garagingAddress;
            }

            const _addressVerification = JSON.stringify(vehicle.garagingAddress).toUpperCase() === JSON.stringify(nextProps.insured.address).toUpperCase()
                ? YES_NO.YES
                : YES_NO.NO;

            const _hasLienholder = (vehicle.lienholder && vehicle.hasLienholder)
                ? YES_NO.YES
                : YES_NO.NO;

            nextProps.meta.propsSource = "";

            return {
                ...prevState,
                vehicle: vehicle,
                wasSalvaged: "undefined" === typeof vehicle.wasSalvaged || null === vehicle.wasSalvaged ? "" : vehicle.wasSalvaged ? YES_NO.YES : YES_NO.NO,
                hasLienholder: _hasLienholder,
                updateLienholder: vehicle.hasLienholder && !vehicle.lienholder ? true : false,
                addressVerification: _addressVerification
            };
        }

        return prevState;
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true }, () => {

            var nextUrl = this.props.vehicleIndex < this.props.vehiclesCount - 1
                ? Utilities.formatUrl(this.props.PATHS.FQ_CARS, (this.props.vehicleIndex + 1).toString())
                : Utilities.formatUrl(this.props.PATHS.FQ_DRIVERS, "");

            this.props.updateAppState({
                quoteId: this.props.quoteId,
                vehicle: this.state.vehicle,
                paymentPlanCode: this.props.paymentPlanCode,
                updateLienholder: this.state.updateLienholder,
                updateGaragingAddress: this.state.updateGaragingAddress,
                addressSynchronization: this.state.addressVerification === YES_NO.YES,
                stateCode: this.props.stateCode
            })
                .then((response) => {
                    this.setState({ updateResult: response.data }, () => {
                        if (!this.state.updateResult?.isSuccess) {
                            this.setState({ ...this.state, updatingStore: false, errors: null });
                        }
                        else if (this.state.updateResult.vehicleChanged || this.state.updateResult.wasSalvaged) {
                            this.props.reloadVehicle({
                                quoteId: this.props.quoteId,
                                vehicleId: this.state.vehicle ? this.state.vehicle.id : 0,
                                vehicleChanged: true,
                                quoteRerated: this.state.updateResult.isRerated
                            })
                            .then(() => this.setState({ updatingStore: false, errors: null }, () => this.props.history.push(nextUrl)),
                                () => this.setState({ ...this.state, updatingStore: false })
                            );
                        }
                        else {
                            this.setState({ updatingStore: false, errors: null }, () => this.props.history.push(nextUrl));
                        }
                    });
                },
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    });
        });
    }

    onAddressVerificationChanged(v: string) {
        const { vehicle } = { ...this.state };
        const currentState = vehicle;

        if (currentState && YES_NO.YES === v) {
            currentState.garagingAddress = this.props.insured.address;
        }

        this.setState({
            ...this.state, vehicle: currentState, addressVerification: v, invalidGaragingAddress: YES_NO.NO === v
        });
    }

    onVinChanged(vin: string) {
        this.vinRecords = [];

        const { vehicle } = { ...this.state };
        const currentVehicle = vehicle;

        if (currentVehicle) {
            currentVehicle.vin = vin;
            currentVehicle.vinVerified = false;
        }

        if (vin.length == 17) {
            this.setState({
                ...this.state, vinValid: true, vehicle: currentVehicle,
                options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
            }, () => {
                    if (vin.match(/^[0-9A-HJ-NPR-Z\\&]{17}$/i)) {
                    this.vinStepHandler(vin);
                }
                else {
                    this.setState({
                        ...this.state, vinValid: false, vehicle: currentVehicle,
                        options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
                    });
                }
            });
        }
        else {
            this.setState({
                ...this.state, vehicle: currentVehicle,
                options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
            });
        }
    }

    getVinRecords(year: number, make: string, model: string, vin: string | null) {
        if (0 === this.vinRecords.length) {
            return this.apiClient
                .getVinRecords(year, make, model, this.props.quoteUid, vin)
                .then((response) => {
                    if (response.data.options && 0 < response.data.options.length) {
                        this.vinRecords = _.map(response.data.options,
                            (o) => {
                                var r = new VinRecord();
                                r.year = o.year;
                                r.make = o.make;
                                r.model = o.model;
                                r.body = o.body;
                                r.engineSize = o.engineSize;
                                r.cylinders = o.cylinders;
                                r.specsCombined = o.specsCombined;
                                r.vin = o.vin;
                                r.antiThft = o.antiThft;
                                r.compSymbol = o.compSymbol;
                                r.collSymbol = o.collSymbol;
                                r.costNewId = o.costNewId;
                                return r;
                            });
                    }
                    return this.vinRecords;
                });
        } else {
            return (Promise.resolve(this.vinRecords));
        }
    }

    vinStepHandler(vin) {
        this.setState({ ...this.state, loadingOptions: true });

        this.getVinRecords(-1, "", "", vin)
            .then(() => {
                if (0 < this.vinRecords.length)
                {
                    const { vehicle } = { ...this.state };
                    const currentVehicle = vehicle;

                    const yearsGroups = _.groupBy(this.vinRecords, "year");
                    const years = _.map(yearsGroups, (records, year) => {
                        return {
                            year: year,
                            count: records.length
                        }
                    });

                    if (1 === years.length) {
                        const yearOption = {
                            value: years[0].year,
                            label: years[0].year,
                            type: VEHICLE_OPTION_TYPES.YEAR
                        };

                        if (currentVehicle)
                        {
                            currentVehicle.year = parseInt(years[0].year, 10);
                        }

                        const extendedSelectedOptions = [...this.state.selectedOptions, yearOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, vehicle: currentVehicle });
                    } else if (1 < years.length) {
                        this.handleNoVehicleOptions();
                    }

                    const makesGroups = _.groupBy(this.vinRecords, "make");
                    const makes = _.map(makesGroups, (records, make) => {
                        return {
                            make: make,
                            count: records.length
                        }
                    });

                    if (1 === makes.length) {
                        const makeOption = {
                            value: makes[0].make,
                            label: makes[0].make,
                            type: VEHICLE_OPTION_TYPES.MAKE
                        };

                        if (currentVehicle)
                        {
                            currentVehicle.make = makes[0].make;
                        }

                        const extendedSelectedOptions = [...this.state.selectedOptions, makeOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, vehicle: currentVehicle });
                    } else if (1 < makes.length) {
                        this.handleNoVehicleOptions();
                    }

                    const modelsGroups = _.groupBy(this.vinRecords, "model");
                    const models = _.map(modelsGroups, (records, model) => {
                        return {
                            model: model,
                            count: records.length
                        }
                    });

                    if (1 === models.length) {
                        const modelOption = {
                            value: models[0].model,
                            label: models[0].model,
                            type: VEHICLE_OPTION_TYPES.MODEL
                        };

                        if (currentVehicle)
                        {
                            currentVehicle.model = models[0].model;
                            currentVehicle.vinVerified = true;
                        }

                        const extendedSelectedOptions = [...this.state.selectedOptions, modelOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, vehicle: currentVehicle, loadingOptions: false });
                    } else if (1 < models.length) {
                        this.handleNoVehicleOptions();
                    }
                }
                else
                {
                    this.handleNoVehicleOptions();
                }
            });
    }

    handleNoVehicleOptions() {
        this.setState({
            ...this.state,
            vinValid: true,
            loadingOptions: false
        });
    }

    onSalvagedChanged(v: string) {
        const { vehicle } = { ...this.state };
        const currentVehicle = vehicle;

        let showSalvageWarning = false;

        if (v === YES_NO.YES
            && !this.state.salvageWarningWasDemonstrated
            && 0 < _.filter(this.state.vehicle?.coverages, (c) => (c.name === "Comprehensive" || c.name === "Collision") && c.limit).length) {
            showSalvageWarning = true;
        }

        let updateLienholder = v === YES_NO.YES ? false : this.state.updateLienholder;
        let invalidLienholderAddress = this.state.invalidLienholderAddress;
        let hasLienholder = this.state.hasLienholder;

        if (currentVehicle) {
            currentVehicle.wasSalvaged = v === YES_NO.YES;

            if (currentVehicle.wasSalvaged) {
                if (currentVehicle.lienholder) {
                    updateLienholder = true;
                }
                currentVehicle.lienholder = null;
                currentVehicle.hasLienholder = false;
                invalidLienholderAddress = false;
                hasLienholder = YES_NO.NO;
            }
        }

        this.setState({ ...this.state, wasSalvaged: v, showSalvageWarning, vehicle: currentVehicle, updateLienholder, invalidLienholderAddress, hasLienholder });
    }

    callbackFunctionUse = (childData: string) => {
        const { vehicle } = { ...this.state };
        const currentState = vehicle;

        if (currentState) {
            currentState.vehicleUse = childData;
        }

        this.setState({ ...this.state, vehicle: currentState });
    };

    hasLienholderCallbackFunction = (childData: string) => {
        const _vehicle = this.state.vehicle;
        let _invalidLienholderAddress = this.state.invalidLienholderAddress;
        if (_vehicle) {
            if (childData === YES_NO.YES) {
                _vehicle.hasLienholder = true;
                if (!_vehicle.lienholder) {
                    _vehicle.lienholder = {
                        name: "",
                        address: {
                            zipCode: "",
                            stateCode: "",
                            city: "",
                            street: "",
                            apartment: ""
                        }
                    }
                }
            }
            else {
                _vehicle.lienholder = null;
                _vehicle.hasLienholder = false;
                _invalidLienholderAddress = false;
            }
        }

        this.setState({ ...this.state, updateLienholder: true, invalidLienholderAddress: _invalidLienholderAddress, vehicle: _vehicle, hasLienholder: childData });
    };

    onLienholderChange(value: string, property: string) {
        this.setStateValue(true, "updateLienholder", this.state);
        if (this.state.vehicle) {
            this.setStateValue(value, property, this.state.vehicle.lienholder);
        }
    }

    onAddressChanged(result: IAddressChangeResult) {
        const { vehicle } = { ...this.state };
        const currentState = vehicle;

        if (currentState) {
            currentState.garagingAddress = result.address;
        }

        const _invalidGaragingAddress = this.state.addressVerification === YES_NO.NO
            ? !(result.hasOwnProperty("isValid") && result.isValid)
            : false;

        this.setState({
            ...this.state, vehicle: currentState, updateGaragingAddress: true, invalidGaragingAddress: _invalidGaragingAddress
        });
    }

    onLienholderAddressChanged(result: IAddressChangeResult) {
        const { vehicle } = { ...this.state };
        const currentState = vehicle;

        if (currentState && currentState.lienholder) {
            currentState.lienholder.address = result.address;
        }

        this.setState({
            ...this.state, vehicle: currentState, updateLienholder: true, invalidLienholderAddress: !(result.hasOwnProperty("isValid") && result.isValid)
        });
    }

    confirmSalvageWarning() {
        this.setState({ ...this.state, salvageWarningWasDemonstrated: true, showSalvageWarning: false });
    }

    render() {
        return (
            <QuoteStep id="car">
                <ValidatableForm hasErrors={null != this.state.errors}>
                    <div className="titleArea">
                        {
                            <h5>
                                Vehicle #{this.props.vehicleIndex + 1}
                            </h5>
                        }
                    </div>

                    {
                        this.state.updateResult && !this.state.updateResult.isSuccess && this.state.updateResult.errors
                            ? <Alert variant={"danger"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review errors below</p>
                                <ul>
                                    {
                                        this.state.updateResult.errors.map((error) => (
                                            <li key={Utilities.uniqueId()}>
                                                <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </Alert>
                            : null
                    }

                    <p className="info">We need to gather some final details about your vehicle to verify we have everything correct:</p>

                    <ValidatableFormGroup
                        label="VIN"
                        maxLength={17}
                        value={this.getValueSafe(this.state.vehicle, "vin")}
                        placeholder="VIN"
                        validated={!this.state.vinValid}
                        validationError={"Incorrect VIN number"}
                        onChange={(e) => { this.onVinChanged(e.target.value.toString().trim()) }} />

                    <div className="info-disclosure address" style={{ display: this.state.vehicle?.vinVerified ? '' : 'none' }}>
                        {this.state.vehicle?.year}
                        {" "}
                        {this.state.vehicle?.make}
                        {" "}
                        {this.state.vehicle?.model}
                    </div>

                    {/*<Question*/}
                    {/*    name="addressVerification"*/}
                    {/*    selectedValue={this.state.addressVerification}*/}
                    {/*    error={validationUtils.getValidationErrorFor(this.state.errors, "addressVerification")}*/}
                    {/*    selected={(v) => this.onAddressVerificationChanged(v)}*/}
                    {/*    items={[YES_NO.YES, YES_NO.NO]}*/}
                    {/*    lable="Your Vehicles must be kept at your home address. Please confirm this is your home and garaging address for the vehicle listed above?" />*/}

                    {/*<div className="info-disclosure address" style={{ display: YES_NO.YES === this.state.addressVerification ? '' : 'none' }}>*/}
                    {/*    {this.state.vehicle?.garagingAddress ? this.state.vehicle?.garagingAddress.street : ""}{this.state.vehicle?.garagingAddress && this.state.vehicle?.garagingAddress.apartment ? ", " + this.state.vehicle?.garagingAddress.apartment : ""}<br />*/}
                    {/*    {this.state.vehicle?.garagingAddress ? this.state.vehicle?.garagingAddress.city : ""}, {this.state.vehicle?.garagingAddress ? this.state.vehicle?.garagingAddress.stateCode : ""} {this.state.vehicle?.garagingAddress ? this.state.vehicle?.garagingAddress.zipCode : ""}<br />*/}
                    {/*</div>*/}

                    {/*{*/}
                    {/*    YES_NO.NO === this.state.addressVerification*/}
                    {/*        ?*/}
                    {/*        <Address label="Where do you keep your car?"*/}
                    {/*            errors={validationUtils.extractErrorsByPrefix(this.state.errors, "garagingAddress.")}*/}
                    {/*            value={this.state.vehicle ? this.state.vehicle.garagingAddress : null}*/}
                    {/*            onChange={this.onAddressChanged} />*/}
                    {/*        : null*/}
                    {/*}*/}

                    {/*<Question*/}
                    {/*    name="vehicleUse"*/}
                    {/*    selected={this.callbackFunctionUse}*/}
                    {/*    selectedValue={this.state.vehicle?.vehicleUse}*/}
                    {/*    error={validationUtils.hasValidationErrorFor(this.state.errors, "vehicleUse") ? this.state.errors["vehicleUse"][0] : ""}*/}
                    {/*    items={[QUOTE_QUESTIONS.VEHICLE_USE.COMMUTING, QUOTE_QUESTIONS.VEHICLE_USE.BUISNESS]}*/}
                    {/*    lable="What is this car's primary use?"*/}
                    {/*    tooltip={{ title: "Business Use", content: "Business or artisan use includes but is not limited to transporting or carrying tools, supplies, equipment, building materials, or persons from home to work or between work locations for a business purpose, or to render services at such location." } as IToolTipOption}*/}
                    {/*/>*/}

                    {/*<Question*/}
                    {/*    name="wasSalvaged"*/}
                    {/*    selectedValue={this.state.wasSalvaged}*/}
                    {/*    error={validationUtils.getValidationErrorFor(this.state.errors, "wasSalvaged")}*/}
                    {/*    selected={(v) => this.onSalvagedChanged(v)}*/}
                    {/*    items={[YES_NO.YES, YES_NO.NO]}*/}
                    {/*    lable="Has this vehicle ever been salvaged or declared a total loss?" />*/}

                    {/*<Collapse in={this.state.showSalvageWarning}>*/}
                    {/*    <div>*/}
                    {/*        <Alert variant={"warning"}>*/}
                    {/*            <h5 className="text-center"><strong className="text-uppercase">Warning!</strong><br /> Salvaged vehicles are not eligible for comprehensive/collision</h5>*/}
                    {/*            <p className="text-center">*/}
                    {/*                <Button*/}
                    {/*                    size="sm"*/}
                    {/*                    onClick={() => this.confirmSalvageWarning()}>*/}
                    {/*                    OK*/}
                    {/*                </Button>*/}
                    {/*            </p>*/}
                    {/*        </Alert>*/}
                    {/*    </div>*/}
                    {/*</Collapse>*/}

                    {
                        this.state.wasSalvaged === YES_NO.NO
                            ?
                            <div>
                                <Question
                                    name="hasLienholder"
                                    selected={this.hasLienholderCallbackFunction}
                                    selectedValue={this.state.hasLienholder}
                                    items={[YES_NO.YES, YES_NO.NO]}
                                    tooltip={{ title: "lienholder", content: "A lienholder generally requires you to purchase comprehensive and collision insurance on the automobile financed to protect their interest. This is usually a condition of the finance agreement." }}
                                    lable="Lienholder?" />

                                {
                                    this.state.hasLienholder === YES_NO.YES
                                        ?
                                        <div>
                                            <div className="titleArea">
                                                <h5>Lienholder</h5>
                                            </div>

                                            <ValidatableFormGroup
                                                label="Name"
                                                value={this.getValueSafe(this.state.vehicle?.lienholder, "name")}
                                                placeholder="Name"
                                                validated={null !== this.state.errors}
                                                disabled={false}
                                                validationError={validationUtils.extractFirstErrorByPrefix(this.state.errors, "lienholder.", "name")}
                                                onChange={(e) => { this.onLienholderChange(e.target.value.toString(), "name") }} />

                                            <Address
                                                noAddressVerification={false}
                                                errors={validationUtils.extractErrorsByPrefix(this.state.errors, "lienholder.address.")}
                                                fieldsVisibility={{ apartment: false }}
                                                isAllFieldsEditable={true}
                                                value={this.state.vehicle?.lienholder ? this.state.vehicle.lienholder.address : null}
                                                onChange={this.onLienholderAddressChanged} />
                                        </div>
                                        : null
                                }
                            </div>
                            :
                            null
                    }

                    <div className="btnContainer">
                        <ButtonContinue
                            disabled={!this.state.vinValid
                                || this.state.loadingOptions
                                || this.state.updatingStore
                                || this.state.invalidGaragingAddress
                                || (this.state.vehicle?.hasLienholder && this.state.invalidLienholderAddress)
                                || (this.state.showSalvageWarning && !this.state.salvageWarningWasDemonstrated) }
                            showLoading={this.state.loadingOptions || this.state.updatingStore}
                            onClick={this.next} />
                    </div>
                </ValidatableForm>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IVehicleStepProps) => {
    var vehicleIndex = !ownProps.match.params["vehicleIndex"]
        ? 0
        : parseInt(ownProps.match.params["vehicleIndex"], 10);
    var vehicle = state.externalQuote?.vehicles[vehicleIndex];


    return {
        vehicleIndex: vehicleIndex,
        quoteId: state.externalQuote?.rtrQuoteId,
        paymentPlanCode: state.externalQuote?.paymentPlanCode,
        vehiclesCount: state.externalQuote?.vehicles.length,
        currentVehicle: vehicle,
        insured: state.externalQuote?.insured,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.CARS)),
        updateAppState: (update: IAppStateUpdate) => dispatch(fullQuoteActions.updateVehicle(update.quoteId, update.vehicle, update.paymentPlanCode, update.updateLienholder, update.updateGaragingAddress, update.addressSynchronization, update.stateCode)),
        reloadVehicle: (update: IVehicleUpdate) => dispatch(fullQuoteActions.reloadVehicle(update.quoteId, update.vehicleId, update.vehicleChanged, update.quoteRerated))
    };
};

export default connect(mapProps, mapDispatch)(Car);